import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Megaphone, Euro, CalendarDays, TrendingUp, Loader2, SkipForward, Hash } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend
} from "recharts";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../components/ui/select";
import { CommTabs } from "../components/communication/CommTabs";
import { getSubscriptions, getCalendarSubscriptions, getSubscriptionBillingStats, getCalendarStats } from "../services/api";

const PLAN_META = {
  basic: { label: "BASIC", color: "#0284c7" },
  branding: { label: "BRANDING", color: "#4f46e5" },
  visibilite: { label: "VISIBILITÉ", color: "#d97706" },
};
const TYPE_META = {
  post: { label: "Posts", color: "#26788A" },
  story: { label: "Stories", color: "#f59e0b" },
  reel: { label: "Reels", color: "#ec4899" },
  video: { label: "Vidéos", color: "#8b5cf6" },
};
const STATUS_META = {
  todo: { label: "À faire", color: "#94a3b8" },
  in_progress: { label: "En cours", color: "#3b82f6" },
  scheduled: { label: "Programmé", color: "#8b5cf6" },
  published: { label: "Publié", color: "#10b981" },
};
const PLATFORM_META = {
  instagram: { label: "Instagram", color: "#ec4899" },
  facebook: { label: "Facebook", color: "#2563eb" },
  tiktok: { label: "TikTok", color: "#0f172a" },
  youtube: { label: "YouTube", color: "#dc2626" },
  linkedin: { label: "LinkedIn", color: "#0284c7" },
};

const fmtEur = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", minimumFractionDigits: 0 }).format(n || 0);

const KpiCard = ({ icon: Icon, label, value, accent, testId }) => (
  <div className="min-w-0 bg-white border border-slate-200 rounded-xl p-4" data-testid={testId}>
    <div className={`flex items-center gap-2 text-xs font-medium uppercase tracking-wide ${accent}`}>
      <Icon size={14} /> {label}
    </div>
    <p className="text-xl sm:text-2xl font-bold text-slate-900 mt-1 break-words [overflow-wrap:anywhere]">{value}</p>
  </div>
);

const ChartCard = ({ title, children, testId }) => (
  <div className="min-w-0 bg-white border border-slate-200 rounded-xl p-4" data-testid={testId}>
    <h3 className="text-sm font-semibold text-slate-700 mb-3">{title}</h3>
    {children}
  </div>
);

const DonutChart = ({ data }) => (
  <ResponsiveContainer width="100%" height={200}>
    <PieChart>
      <Pie data={data} dataKey="value" nameKey="name" innerRadius={45} outerRadius={70} paddingAngle={2}>
        {data.map((d, i) => <Cell key={i} fill={d.color} />)}
      </Pie>
      <Tooltip />
      <Legend iconSize={8} wrapperStyle={{ fontSize: "11px" }} />
    </PieChart>
  </ResponsiveContainer>
);

const RankList = ({ items, unit, colorFor }) => {
  const max = Math.max(...items.map((i) => i.value), 1);
  return (
    <div className="space-y-2.5">
      {items.map((it) => (
        <div key={it.name}>
          <div className="flex justify-between text-xs mb-1">
            <span className="font-medium text-slate-700">{it.name}</span>
            <span className="text-slate-500">{it.display || `${it.value} ${unit}`}</span>
          </div>
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full rounded-full" style={{ width: `${(it.value / max) * 100}%`, background: colorFor?.(it) || "#26788A" }} />
          </div>
        </div>
      ))}
      {items.length === 0 && <p className="text-sm text-slate-400">Aucune donnée</p>}
    </div>
  );
};

export default function CommunicationStatsPage({ calendarOnly = false }) {
  const [subscriptions, setSubscriptions] = useState([]);
  const [selectedSub, setSelectedSub] = useState("all");
  const [year, setYear] = useState("all");
  const [billing, setBilling] = useState(null);
  const [calStats, setCalStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (calendarOnly ? getCalendarSubscriptions() : getSubscriptions()).then((res) => setSubscriptions(res.data)).catch(() => {});
  }, [calendarOnly]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const subParam = selectedSub !== "all" ? { subscription_id: selectedSub } : {};
      const calParams = { ...subParam };
      if (year !== "all") calParams.year = year;
      const [bRes, cRes] = await Promise.all([
        calendarOnly ? Promise.resolve({ data: null }) : getSubscriptionBillingStats(subParam),
        calendarOnly ? getCalendarStats(calParams) : Promise.resolve({ data: null }),
      ]);
      setBilling(bRes.data);
      setCalStats(cRes.data);
    } catch (err) {
      toast.error("Erreur de chargement des statistiques");
    } finally {
      setLoading(false);
    }
  }, [selectedSub, year, calendarOnly]);

  useEffect(() => { load(); }, [load]);

  const planDonut = billing ? Object.entries(billing.by_plan).map(([k, v]) => ({
    name: PLAN_META[k]?.label || k, value: v, color: PLAN_META[k]?.color || "#26788A",
  })) : [];
  const typeDonut = calStats ? Object.entries(calStats.by_type).map(([k, v]) => ({
    name: TYPE_META[k]?.label || k, value: v, color: TYPE_META[k]?.color || "#26788A",
  })) : [];
  const statusDonut = calStats ? Object.entries(calStats.by_status).map(([k, v]) => ({
    name: STATUS_META[k]?.label || k, value: v, color: STATUS_META[k]?.color || "#94a3b8",
  })) : [];
  const platformDonut = calStats ? Object.entries(calStats.by_platform).map(([k, v]) => ({
    name: PLATFORM_META[k]?.label || k, value: v, color: PLATFORM_META[k]?.color || "#26788A",
  })) : [];
  const lastWeeks = calStats ? calStats.by_week.slice(-12) : [];

  return (
    <div className="min-w-0 p-4 lg:p-8 max-w-6xl mx-auto" data-testid={calendarOnly ? "diffusion-stats-page" : "comm-stats-page"}>
      <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
        {calendarOnly ? <CalendarDays size={24} className="text-[#26788A] shrink-0" /> : <Megaphone size={24} className="text-[#26788A] shrink-0" />}{calendarOnly ? "Statistiques de diffusion" : "Communication"}
      </h1>
      <p className="text-sm text-slate-500 mb-4">{calendarOnly ? "Publications et plateformes" : "Statistiques — facturation des forfaits"}</p>

      {calendarOnly ? <Link to="/calendrier-diffusion" className="inline-flex min-h-11 text-sm items-center text-[#26788A] mb-4" data-testid="diffusion-stats-back">← Calendrier de diffusion</Link> : <CommTabs />}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <Select value={selectedSub} onValueChange={setSelectedSub}>
          <SelectTrigger className="w-full sm:w-64 min-h-11" data-testid="stats-client-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les clients</SelectItem>
            {subscriptions.map((s) => (
              <SelectItem key={s.id} value={s.id}>{s.customer_name} · {PLAN_META[s.plan]?.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {calendarOnly && <Select value={year} onValueChange={setYear}>
          <SelectTrigger className="w-36" data-testid="stats-year-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes années</SelectItem>
            {(calStats?.available_years || []).map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
          </SelectContent>
        </Select>}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 size={28} className="animate-spin text-slate-300" /></div>
      ) : (
        <div className="space-y-8">
          {/* ===== FACTURATION ===== */}
          {!calendarOnly && <section data-testid="billing-stats-section">
            <h2 className="text-base font-bold text-slate-800 flex items-center gap-2 mb-3">
              <Euro size={17} className="text-[#26788A]" /> Facturation des forfaits
            </h2>
            <div className="grid sm:grid-cols-3 gap-3 mb-4">
              <KpiCard icon={TrendingUp} label="Total facturé HT" value={fmtEur(billing?.total_billed_ht)} accent="text-[#26788A]" testId="kpi-total-billed" />
              <KpiCard icon={Euro} label="Mois facturés" value={billing?.months_billed ?? 0} accent="text-emerald-600" testId="kpi-months-billed" />
              <KpiCard icon={SkipForward} label="Mois passés" value={billing?.months_skipped ?? 0} accent="text-slate-500" testId="kpi-months-skipped" />
            </div>
            <div className="grid lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2">
                <ChartCard title="Chiffre d'affaires facturé par mois (€ HT)" testId="chart-billing-by-month">
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={billing?.by_month || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip formatter={(v) => fmtEur(v)} />
                      <Bar dataKey="amount" name="€ HT" fill="#26788A" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>
              </div>
              <ChartCard title="Répartition par forfait" testId="chart-billing-by-plan">
                {planDonut.length > 0 ? <DonutChart data={planDonut} /> : <p className="text-sm text-slate-400 py-8 text-center">Aucune donnée</p>}
              </ChartCard>
            </div>
            <div className="mt-4">
              <ChartCard title="Facturation par client" testId="chart-billing-by-client">
                <RankList
                  items={(billing?.by_client || []).map((c) => ({
                    name: c.name, value: c.amount,
                    display: `${fmtEur(c.amount)} · ${c.months} mois`,
                    plan: c.plan,
                  }))}
                  unit="€"
                  colorFor={(it) => PLAN_META[it.plan]?.color}
                />
              </ChartCard>
            </div>
          </section>}

          {/* ===== CALENDRIER DE DIFFUSION ===== */}
          {calendarOnly && <section data-testid="calendar-stats-section">
            <h2 className="text-base font-bold text-slate-800 flex items-center gap-2 mb-3">
              <CalendarDays size={17} className="text-[#26788A]" /> Diffusion (calendriers)
            </h2>
            <div className="grid sm:grid-cols-3 gap-3 mb-4">
              <KpiCard icon={Hash} label="Publications totales" value={calStats?.total_posts ?? 0} accent="text-[#26788A]" testId="kpi-total-posts" />
              <KpiCard icon={CalendarDays} label="Clients avec diffusion" value={calStats?.by_client?.length ?? 0} accent="text-indigo-600" testId="kpi-clients-diffusion" />
              <KpiCard
                icon={TrendingUp}
                label="Top client diffusion"
                value={calStats?.by_client?.[0] ? `${calStats.by_client[0].name} (${calStats.by_client[0].count})` : "—"}
                accent="text-amber-600"
                testId="kpi-top-client"
              />
            </div>

            <div className="grid lg:grid-cols-3 gap-4 mb-4">
              <div className="lg:col-span-2">
                <ChartCard title="Publications par mois" testId="chart-posts-by-month">
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={calStats?.by_month || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="count" name="Publications" fill="#4f46e5" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>
              </div>
              <ChartCard title="Par type de diffusion" testId="chart-posts-by-type">
                {typeDonut.length > 0 ? <DonutChart data={typeDonut} /> : <p className="text-sm text-slate-400 py-8 text-center">Aucune donnée</p>}
              </ChartCard>
            </div>

            <div className="grid lg:grid-cols-3 gap-4 mb-4">
              <ChartCard title="Par plateforme" testId="chart-posts-by-platform">
                {platformDonut.length > 0 ? <DonutChart data={platformDonut} /> : <p className="text-sm text-slate-400 py-8 text-center">Aucune donnée</p>}
              </ChartCard>
              <ChartCard title="Par statut" testId="chart-posts-by-status">
                {statusDonut.length > 0 ? <DonutChart data={statusDonut} /> : <p className="text-sm text-slate-400 py-8 text-center">Aucune donnée</p>}
              </ChartCard>
              <ChartCard title="Par jour de la semaine" testId="chart-posts-by-weekday">
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={calStats?.by_weekday || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="day" tick={{ fontSize: 9 }} />
                    <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="count" name="Publications" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </div>

            <div className="grid lg:grid-cols-2 gap-4">
              <ChartCard title="Diffusion par client" testId="chart-posts-by-client">
                <RankList
                  items={(calStats?.by_client || []).map((c) => ({
                    name: c.name, value: c.count,
                    display: `${c.count} publication${c.count > 1 ? "s" : ""}`,
                    plan: c.plan,
                  }))}
                  unit="publications"
                  colorFor={(it) => PLAN_META[it.plan]?.color}
                />
              </ChartCard>
              <ChartCard title="12 dernières semaines" testId="chart-posts-by-week">
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={lastWeeks}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="week" tick={{ fontSize: 9 }} />
                    <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="count" name="Publications" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
                {Object.keys(calStats?.by_year || {}).length > 0 && (
                  <div className="flex gap-2 mt-2 flex-wrap">
                    {Object.entries(calStats.by_year).sort().map(([y, c]) => (
                      <span key={y} className="text-xs bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full">
                        {y} : <strong>{c}</strong>
                      </span>
                    ))}
                  </div>
                )}
              </ChartCard>
            </div>
          </section>}
        </div>
      )}
    </div>
  );
}
